import React, { useState, useRef } from "react";
import { COLORS, STATUS_COLORS } from "../../../theme/colors";
import Icon from "../../../components/Icon";

const DraggableNode = ({ node, isSelected, onClick, onDragStart, onDrag, onDragEnd, onPortMouseDown, onPortMouseUp, onDelete, onLabelChange, connectingFrom, mode = "author" }) => {
  // mode="readonly" — Detail Viewer GraphTab (B5/B8 per agent-detail-viewer-v1.md §10).
  // Same visual treatment as author mode minus drag handle / ports / delete.
  const isReadonly = mode === "readonly";
  const nodeRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  // C10 — inline node renaming. Double-click the label area to edit;
  // Enter/blur commits via onLabelChange; Esc reverts. Disabled in
  // readonly mode (Viewer's GraphTab) and when no callback is wired.
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(node.label);
  const inputRef = useRef(null);
  const isHub = node.type === "llm";
  const isAgent = node.type === "agent";
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (isReadonly) return;
    if (e.target.dataset.port) return;
    e.stopPropagation();
    const rect = nodeRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setIsDragging(true);
    onDragStart(node.id);
    const handleMove = (ev) => {
      onDrag(node.id, ev.clientX - dragOffset.current.x, ev.clientY - dragOffset.current.y);
    };
    const handleUp = () => {
      setIsDragging(false);
      onDragEnd(node.id);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  return (
    <div ref={nodeRef}
      data-node-id={node.id}
      data-node-type={node.type}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(node.id); }} onMouseDown={handleMouseDown}
      style={{
        position: "absolute", left: node.x, top: node.y,
        background: COLORS.surface,
        border: `${isAgent ? 2 : 1.5}px solid ${isSelected ? node.color : COLORS.border}`,
        borderRadius: isHub ? 18 : isAgent ? 16 : 14,
        padding: isHub ? "18px 24px" : isAgent ? "16px 22px" : "14px 18px",
        cursor: isReadonly ? "pointer" : (isDragging ? "grabbing" : "grab"),
        userSelect: "none",
        opacity: node.enabled === false ? 0.5 : 1,
        minWidth: isHub ? 180 : isAgent ? 170 : 140,
        transition: isDragging ? "none" : "border-color 0.2s, box-shadow 0.2s",
        boxShadow: isDragging ? `0 8px 32px rgba(0,0,0,0.5), 0 0 24px ${node.color}22`
          : isAgent && !isDragging ? `0 0 16px ${node.color}22, inset 0 0 12px ${node.color}08`
          : isHub ? `0 0 20px ${COLORS.accentGlow}, 0 0 60px rgba(79,70,229,0.08)`
          : isSelected ? `0 0 20px ${node.color}33` : `0 2px 8px rgba(0,0,0,0.3)`,
        animation: isHub && !isDragging ? "hubGlow 3s ease-in-out infinite" : "none",
        zIndex: isDragging ? 100 : isSelected ? 10 : 1,
        transform: isDragging ? "scale(1.04)" : "scale(1)",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: isHub ? 44 : 32, height: isHub ? 44 : 32, borderRadius: isHub ? 12 : 8,
          background: `${node.color}18`, border: `1px solid ${node.color}33`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: isHub ? 20 : 16,
        }}>
          {node.iconName
            ? <Icon name={node.iconName} size={isHub ? 22 : 16} color={node.color} />
            : node.icon /* legacy glyph fallback for trees saved before M1.7b */}
        </div>
        <div>
          {editing && !isReadonly && onLabelChange ? (
            <input
              ref={inputRef}
              autoFocus
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onBlur={() => {
                if (draftLabel.trim() && draftLabel !== node.label) {
                  onLabelChange(node.id, draftLabel.trim());
                }
                setEditing(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (draftLabel.trim() && draftLabel !== node.label) {
                    onLabelChange(node.id, draftLabel.trim());
                  }
                  setEditing(false);
                } else if (e.key === "Escape") {
                  setDraftLabel(node.label);
                  setEditing(false);
                }
                e.stopPropagation();
              }}
              style={{
                fontSize: isHub ? 14 : 12, fontWeight: isHub ? 700 : 600,
                color: COLORS.text, background: COLORS.surface,
                border: `1px solid ${node.color}55`, borderRadius: 4,
                padding: "2px 5px", outline: "none", letterSpacing: "0.01em",
                width: isHub ? 160 : 120,
              }}
            />
          ) : (
            <div
              onDoubleClick={(e) => {
                if (isReadonly || !onLabelChange) return;
                e.stopPropagation();
                setDraftLabel(node.label);
                setEditing(true);
              }}
              style={{ fontSize: isHub ? 14 : 12, fontWeight: isHub ? 700 : 600, color: COLORS.text, letterSpacing: "0.01em" }}
              title={!isReadonly && onLabelChange ? "Double-click to rename" : undefined}
            >{node.label}</div>
          )}
          <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>{node.type}</div>
        </div>
      </div>
      {isHub && (
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: "140%", height: "140%", borderRadius: "50%",
          background: `radial-gradient(ellipse at center, ${COLORS.accentGlow} 0%, transparent 70%)`,
          pointerEvents: "none", zIndex: -1, opacity: 0.5,
        }} />
      )}
      {/* Agent node status indicator */}
      {isAgent && node.agentConfig?.agentStatus && (
        <div style={{
          position: "absolute", top: -4, left: -4,
          width: 10, height: 10, borderRadius: "50%",
          background: STATUS_COLORS[node.agentConfig.agentStatus] || COLORS.textDim,
          border: `2px solid ${COLORS.surface}`,
          boxShadow: `0 0 4px ${(STATUS_COLORS[node.agentConfig.agentStatus] || COLORS.textDim)}66`,
          zIndex: 10,
        }} />
      )}
      {/* Ports + delete button — author mode only. Viewer (readonly) skips them. */}
      {!isReadonly && (
        <>
          <div data-port="in"
            onMouseUp={(e) => { e.stopPropagation(); onPortMouseUp(node.id); }}
            style={{
              position: "absolute", left: -6, top: "50%", transform: "translateY(-50%)",
              width: 12, height: 12, borderRadius: "50%", background: COLORS.surface,
              border: `2px solid ${connectingFrom ? COLORS.green : COLORS.border}`,
              cursor: "crosshair", zIndex: 5,
              boxShadow: connectingFrom ? `0 0 8px ${COLORS.greenGlow}` : "none",
              transition: "all 0.2s",
            }} />
          <div data-port="out"
            onMouseDown={(e) => { e.stopPropagation(); onPortMouseDown(node.id); }}
            style={{
              position: "absolute", right: -6, top: "50%", transform: "translateY(-50%)",
              width: 12, height: 12, borderRadius: "50%", background: COLORS.surface,
              border: `2px solid ${node.color}`,
              cursor: "crosshair", zIndex: 5,
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-50%) scale(1.4)"; e.currentTarget.style.boxShadow = `0 0 10px ${node.color}55`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(-50%) scale(1)"; e.currentTarget.style.boxShadow = "none"; }}
          />
          {isSelected && (
            <div onClick={(e) => { e.stopPropagation(); onDelete(node.id); }} style={{
              position: "absolute", top: -8, right: -8, width: 18, height: 18, borderRadius: "50%",
              background: COLORS.pink, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, color: "#fff", cursor: "pointer", zIndex: 10,
              boxShadow: `0 2px 8px ${COLORS.pinkGlow}`,
            }}>{"\u2715"}</div>
          )}
        </>
      )}
    </div>
  );
};

export default DraggableNode;
